import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { CannonBall } from "./cannon_ball.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { Trajectory } from "./trajectory.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { Renderer } from "../core/renderer.js";
import { toCreasedNormals } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { LayoutScalerLocal } from "../layout_scaler/layout_scaler.js";
import { BallTuner } from "../debug/ball_tuner.js";
import { CannonConfigService } from "../cannon_config/cannon_config_service.js";

export class Cannon
{
  private static readonly MODEL_FORWARD_LENGTH = 0.9;

  private static readonly VISUAL_PITCH_OFFSET = THREE.MathUtils.degToRad( 12 );
  // Local point on the crown side of the cannon. Both yaw and pitch rotate
  // around this anchor instead of around the model's geometric centre.
  private static readonly ROTATION_ANCHOR = new THREE.Vector3( 0, 0.00, 0.25 );
  private static readonly BIG_BALL_CHARGE_DAMPING = 10;
  private static readonly BIG_BALL_RELEASE_DAMPING = 14;
  private static readonly NORMAL_SHOT_RECHARGE_DAMPING = 7;
  private static readonly NORMAL_SHOT_REAR_MORPH_STRENGTH = 1.12;
  private static readonly BIG_BALL_REAR_MORPH_STRENGTH = 2.4;
  private static readonly FRONT_MORPH_EXPANSION = 0.21;
  private static readonly BIG_BALL_FRONT_MORPH_STRENGTH = 2;
  private static readonly BIG_BALL_FRONT_AXIS_Z = 3.875;

  private readonly raycaster = new THREE.Raycaster();
  private readonly mouse = new THREE.Vector2();

  private readonly pivot = new THREE.Group();
  private readonly pitch_pivot = new THREE.Group();
  private readonly muzzle = new THREE.Object3D();

  private static readonly POSITION = new THREE.Vector3(
    0,
    // Lift the whole cannon closer to the playfield, matching the reference
    // composition where the muzzle sits clearly above the bottom UI edge.
    1.20,
    7.5
  );

  private get ball_speed(): number
  {
    return BallTuner.applySpeed( CannonConfigService.get().ball_speed );
  }

  private readonly aim_plane: THREE.Plane;
  private readonly aim_filter: ( collider: RAPIER.Collider ) => boolean;
  private readonly aim_ray = new RAPIER.Ray( { x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 0 } );
  private readonly model: THREE.Object3D;
  private readonly model_base_position = new THREE.Vector3();
  private readonly big_ball_morphs: Array<{
    influences: number[];
    index: number;
    normal_shot_strength: number;
    big_ball_strength: number;
  }> = [];

  private static readonly ARRIVAL_RADIUS = 0.7;
  private static readonly PATH_STEPS = 14;

  private barrel_pitch = 0;
  private recoil = 0;
  private rocket_spawn_index = 0;
  // A regular cannon rests in its subtly inflated shape. Firing collapses it
  // to zero, then updateBigBallCharge smoothly restores this value to one.
  private normal_shot_charge = 1;
  private big_ball_charge = 0;
  private big_ball_charge_target = 0;

  private last_pose_pitch = Number.NaN;
  private last_pose_yaw = Number.NaN;
  private last_pose_recoil = Number.NaN;
  private last_pose_normal_shot_charge = Number.NaN;
  private last_pose_big_ball_charge = Number.NaN;

  private barrel!: THREE.Object3D;
  private balls: CannonBall[] = [];
  private readonly far_dir = new THREE.Vector3();
  private far_limit = Infinity;

  constructor(
    plane: THREE.Plane,
    aim_filter: ( collider: RAPIER.Collider ) => boolean
  )
  {
    this.aim_plane = plane;
    this.aim_filter = aim_filter;

    this.model = this.createModel();
    this.applyBigBallMorph();
    this.model.position.copy( Cannon.ROTATION_ANCHOR ).multiplyScalar( -1 );
    this.model_base_position.copy( this.model.position );
    this.barrel = this.model;

    this.setupPivot();
    this.setupMuzzle();
  }

  private createModel(): THREE.Object3D
  {
    const visual = ResourceManager.getModel(
      ResourceID.CannonModel
    );

    visual.updateMatrixWorld( true );
    const authored_box = new THREE.Box3().setFromObject( visual );
    const authored_size = authored_box.getSize( new THREE.Vector3() );
    if ( authored_size.x <= 0 )
      throw new Error( "Cannon model has invalid dimensions" );

    // FBX exports may carry a baked scale of 1, 100, or another DCC-specific
    // value. Normalize from the actual geometry instead of relying on it.
    visual.scale.setScalar( Cannon.MODEL_FORWARD_LENGTH / authored_size.x );

    const base_map = ResourceManager.getTexture( ResourceID.CannonModelBaseMap );
    const material = new THREE.MeshPhongMaterial( {
      map: base_map,
      normalMap: ResourceManager.getNormalMap( ResourceID.CannonModelNormalMap ),
      // Keep the painted detail, but do not let the normal map and a tight
      // specular highlight split the muzzle rim into a dark band and a
      // bright toothed face.
      normalScale: new THREE.Vector2( 0.1, 0.1 ),
      // The cannon is painted rather than polished: a moderately rough
      // dielectric surface keeps highlights soft and subdued.

      // Texture-coloured fill gently lifts the shadowed side of the rim. It
      // preserves the authored colours and avoids a separate metallic effect.
      emissive: new THREE.Color( 0xffffff ),
      emissiveMap: base_map,
      // Keep downward-facing rim geometry coloured instead of letting it
      // collapse to black at the cannon's maximum pitch.
      emissiveIntensity: 0.48,
      shininess: 35,
      specular: new THREE.Color( 0x333333 )
    } );

    visual.traverse( child =>
    {
      if ( child instanceof THREE.Mesh )
      {
        child.geometry = toCreasedNormals(
          child.geometry.clone(),
          THREE.MathUtils.degToRad( 115 )
        );
        this.addBigBallFrontMorph( child );
        child.updateMorphTargets();

        for ( const morph_name of [ "fair", "big_ball_front" ] )
        {
          const morph_index = child.morphTargetDictionary?.[morph_name];
          if ( morph_index === undefined || !child.morphTargetInfluences )
            continue;

          child.morphTargetInfluences[morph_index] = 0;
          this.big_ball_morphs.push( {
            influences: child.morphTargetInfluences,
            index: morph_index,
            normal_shot_strength: morph_name === "fair"
              ? Cannon.NORMAL_SHOT_REAR_MORPH_STRENGTH
              : 1,
            big_ball_strength: morph_name === "fair"
              ? Cannon.BIG_BALL_REAR_MORPH_STRENGTH
              : Cannon.BIG_BALL_FRONT_MORPH_STRENGTH,
          } );
        }
        child.material = material;
        // Almost half of every shadow-map pass was this one mesh, and its own
        // shadow only ever reached a sliver of grass at the very bottom edge.
        child.castShadow = false;
        // Prevent the muzzle rim from projecting a harsh black self-shadow
        // onto the barrel when the cannon reaches its highest pitch.
        child.receiveShadow = false;
      }
    } );

    visual.updateMatrixWorld( true );
    const visual_center = new THREE.Box3()
      .setFromObject( visual )
      .getCenter( new THREE.Vector3() );
    visual.position.sub( visual_center );

    const model = new THREE.Group();
    model.add( visual );
    return model;
  }

  private addBigBallFrontMorph( mesh: THREE.Mesh ): void
  {
    const geometry = mesh.geometry;
    const position = geometry.getAttribute( "position" );
    if ( !position )
      return;

    const deltas = new Float32Array( position.count * 3 );
    for ( let i = 0; i < position.count; i++ )
    {
      const y = position.getY( i );
      // The FBX barrel points along +Y. Blend the deformation in over the
      // first two authored units so the front grows out of the body smoothly.
      const weight = THREE.MathUtils.smoothstep( y, 0, 2 );
      if ( weight === 0 )
        continue;

      const expansion = Cannon.FRONT_MORPH_EXPANSION * weight;
      deltas[i * 3] = position.getX( i ) * expansion;
      deltas[i * 3 + 1] = 0;
      deltas[i * 3 + 2] = (
        position.getZ( i ) - Cannon.BIG_BALL_FRONT_AXIS_Z
      ) * expansion;
    }

    const morph = new THREE.Float32BufferAttribute( deltas, 3 );
    morph.name = "big_ball_front";
    const position_morphs = geometry.morphAttributes.position ?? [];
    geometry.morphAttributes.position = [ ...position_morphs, morph ];
    geometry.morphTargetsRelative = true;
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
  }

  private setupPivot(): void
  {
    // Move the outer pivot to the crown and offset the model back by the same
    // amount. The neutral pose stays in exactly the same world position, but
    // aiming now swings the barrel from the crown side.
    this.pivot.position.copy( Cannon.composedPivot() );

    this.pitch_pivot.add( this.model );
    this.pivot.add( this.pitch_pivot );

    SceneManager.world.add(
      this.pivot
    );
  }

  public setVisible( visible: boolean ): void
  {
    this.pivot.visible = visible;
  }

  private setupMuzzle(): void
  {
    this.model.add( this.muzzle );

    this.muzzle.position.set( 0, 0.1, -Cannon.MODEL_FORWARD_LENGTH * 0.7 );
  }

  /** Where a shot at this pointer would go. */
  public getTarget(
    event: PointerEvent
  ): THREE.Vector3 | null
  {
    const bounds = Renderer.getCanvasBounds();

    this.mouse.x =
      ( ( event.clientX - bounds.left ) / bounds.width ) * 2 - 1;

    this.mouse.y =
      -( ( event.clientY - bounds.top ) / bounds.height ) * 2 + 1;

    this.raycaster.setFromCamera(
      this.mouse,
      SceneManager.camera
    );

    const hit_target = this.getRaycastTarget();

    if ( hit_target !== null )
      return hit_target;

    const target = new THREE.Vector3();

    if (
      !this.raycaster.ray.intersectPlane(
        this.aim_plane,
        target
      )
    )
    {
      return null;
    }

    return target;
  }

  // The ballistic arc passes exactly through the returned point, so aiming at
  // whatever collider is under the finger makes the ball hit the tapped spot
  // on any table. Taps into empty space fall back to the aim plane (lob to
  // the landing point).
  private getRaycastTarget(): THREE.Vector3 | null
  {
    const ray = this.raycaster.ray;

    this.aim_ray.origin.x = ray.origin.x;
    this.aim_ray.origin.y = ray.origin.y;
    this.aim_ray.origin.z = ray.origin.z;
    this.aim_ray.dir.x = ray.direction.x;
    this.aim_ray.dir.y = ray.direction.y;
    this.aim_ray.dir.z = ray.direction.z;

    const hit = PhysicsWorld.getWorld().castRay(
      this.aim_ray,
      500,
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      this.aim_filter
    );

    if ( !hit )
      return null;

    const point = this.aim_ray.pointAt( hit.timeOfImpact );
    const target = new THREE.Vector3( point.x, point.y, point.z );

    const spawn = this.getSpawnPosition();
    const dx = target.x - spawn.x;
    const dz = target.z - spawn.z;

    // A target (almost) directly above the muzzle degenerates the ballistic
    // solve (time = distance / velocity_xz) — let the plane handle it.
    if ( dx * dx + dz * dz < 0.25 )
      return null;

    return target;
  }

  public isPathClear( target: THREE.Vector3 ): boolean
  {
    const spawn = this.getSpawnPosition();
    const velocity = this.calculateBallisticVelocity( spawn, target, this.ball_speed );

    const speed_xz = Math.hypot( velocity.x, velocity.z );
    const distance_xz = Math.hypot( target.x - spawn.x, target.z - spawn.z );

    if ( speed_xz < 1e-4 )
      return true;

    const flight = distance_xz / speed_xz;
    const gravity = PhysicsWorld.GRAVITY;

    let previous = spawn.clone();

    for ( let step = 1; step <= Cannon.PATH_STEPS; step++ )
    {
      const t = ( flight * step ) / Cannon.PATH_STEPS;

      const point = new THREE.Vector3(
        spawn.x + velocity.x * t,
        spawn.y + velocity.y * t - 0.5 * gravity * t * t,
        spawn.z + velocity.z * t
      );

      const segment = point.clone().sub( previous );
      const length = segment.length();

      if ( length > 1e-6 )
      {
        segment.divideScalar( length );

        this.aim_ray.origin.x = previous.x;
        this.aim_ray.origin.y = previous.y;
        this.aim_ray.origin.z = previous.z;
        this.aim_ray.dir.x = segment.x;
        this.aim_ray.dir.y = segment.y;
        this.aim_ray.dir.z = segment.z;

        const hit = PhysicsWorld.getWorld().castRay(
          this.aim_ray,
          length,
          true,
          undefined,
          undefined,
          undefined,
          undefined,
          this.aim_filter
        );

        if ( hit )
        {
          const at = this.aim_ray.pointAt( hit.timeOfImpact );
          const distance = Math.hypot( at.x - target.x, at.y - target.y, at.z - target.z );

          return distance <= Cannon.ARRIVAL_RADIUS;
        }
      }

      previous = point;
    }

    return true;
  }

  private getSpawnPosition(): THREE.Vector3
  {
    const spawn = new THREE.Vector3();

    this.muzzle.getWorldPosition(
      spawn
    );

    return spawn;
  }

  private calculateBallisticVelocity(
    start: THREE.Vector3,
    target: THREE.Vector3,
    speed: number
  ): THREE.Vector3
  {
    const gravity = PhysicsWorld.GRAVITY;
    const diff = target.clone().sub(
      start
    );

    const horizontal = new THREE.Vector3(
      diff.x,
      0,
      diff.z
    );

    const distance = horizontal.length();
    const height = diff.y;

    const angle =
      THREE.MathUtils.degToRad(
        45
      );

    const velocity_xz = speed * Math.cos( angle );
    const time = distance / velocity_xz;
    const velocity_y = ( height + 0.5 * gravity * time * time ) / time;

    horizontal.normalize();

    return new THREE.Vector3(
      horizontal.x * velocity_xz,
      velocity_y,
      horizontal.z * velocity_xz
    );
  }

  private updateRotation( velocity: THREE.Vector3 ): void
  {
    const direction = velocity.clone().normalize();

    const yaw = Math.atan2( direction.x, -direction.z );
    const pitch = Math.asin( direction.y );

    this.pivot.rotation.y = -yaw;

    this.barrel_pitch = THREE.MathUtils.clamp(
      pitch + Cannon.VISUAL_PITCH_OFFSET,
      -0.65,
      0.85
    );
  }

  public aim(
    event: PointerEvent
  ): void
  {
    const target = this.getTarget( event );

    if ( target === null )
      return;

    const spawn = this.getSpawnPosition();
    const velocity = this.calculateBallisticVelocity( spawn, target, this.ball_speed );

    // this.trajectory.show(
    //   spawn,
    //   velocity
    // );

    this.updateRotation( velocity );
  }

  public aimAtWorldPoint( target: THREE.Vector3 ): void
  {
    const spawn = this.getSpawnPosition();
    const velocity = this.calculateBallisticVelocity( spawn, target, this.ball_speed );
    this.updateRotation( velocity );
    this.update( 0 );
  }

  public getHighlightObject(): THREE.Object3D
  {
    return this.pivot;
  }

  public fire(
    event: PointerEvent,
    ball_size_multiplier = 1,
    ball_mass_multiplier = 1,
    is_firework = false
  ): THREE.Vector3 | null
  {
    const target = this.getTarget( event );

    if ( target === null )
      return null;

    this.fireAtWorldPoint( target, ball_size_multiplier, ball_mass_multiplier, is_firework );
    return target.clone();
  }

  // cannon.ts — додати поруч із fire()
  public fireAtWorldPoint(
    target: THREE.Vector3,
    ball_size_multiplier = 1,
    ball_mass_multiplier = 1,
    is_firework = false,
    yaw_offset_radians = 0,
    is_passive_rocket = false
  ): void
  {
    let spawn = this.getSpawnPosition();

    if ( is_firework )
    {
      const rocket_index = this.rocket_spawn_index % 3;
      this.rocket_spawn_index++;
      
      const initial_spawn = new THREE.Vector3( 0, 0, 8 );
      let cp1: THREE.Vector3;
      let cp2: THREE.Vector3;

      // Scale rocket arc magnitude dynamically using LayoutScalerLocal (1.0 on desktop = 6.0 max arc, ~0.70 on mobile = 4.2 max arc)
      const scale = LayoutScalerLocal.fromWindow().getScale();
      const arc_scale = THREE.MathUtils.clamp( scale, 0.70, 1.0 );
      
      const max_arc_x = 5.5 * arc_scale;
      const min_arc_x = 3 * arc_scale;
      const cp2_offset_wide = 3 * arc_scale;
      const cp2_offset_med = 1.2 * arc_scale;

      if ( rocket_index === 0 )
      {
        // 1. Right wide arc (relative to camera)
        cp1 = new THREE.Vector3( max_arc_x, 7, 6 ); 
        cp2 = new THREE.Vector3( target.x - cp2_offset_wide, target.y + 3, target.z + 3 );
      }
      else if ( rocket_index === 1 )
      {
        // 2. Left wide arc
        cp1 = new THREE.Vector3( -max_arc_x, 5, 6 );
        cp2 = new THREE.Vector3( target.x + cp2_offset_wide, target.y + 1, target.z + 4 );
      }
      else
      {
        // 3. Left medium arc
        cp1 = new THREE.Vector3( -min_arc_x, 3.5, 6 );
        cp2 = new THREE.Vector3( target.x + cp2_offset_med, target.y + 1, target.z + 1 );
      }

      const rocket = new CannonBall( initial_spawn, new THREE.Vector3(), 1, ball_mass_multiplier, true, is_passive_rocket );
      
      rocket.setRocketBezierTrajectory( {
        start: initial_spawn,
        end: target,
        controlPoint1: cp1,
        controlPoint2: cp2,
        durationSeconds: 1.2,
        easing: p => Math.pow( p, 3 )
      } );
      
      this.balls.push( rocket );
      return;
    }

    const velocity = this.calculateBallisticVelocity( spawn, target, this.ball_speed );
    if ( yaw_offset_radians !== 0 )
      velocity.applyAxisAngle( new THREE.Vector3( 0, 1, 0 ), yaw_offset_radians );

    this.updateRotation( velocity );
    this.recoil = -0.5;

    // A very fast tap can happen before the charge interpolation reaches its
    // end. The authored "fair" morph must still be fully inflated on the
    // actual big-ball firing frame before the booster starts releasing it.
    if ( ball_size_multiplier > 1 )
    {
      this.big_ball_charge = 1;
      this.applyBigBallMorph();
    }
    else
    {
      // A regular shot briefly deflates the cannon. It then visibly inflates
      // back to the subtler resting profile during the following frames.
      this.normal_shot_charge = 0;
      this.applyBigBallMorph();
    }

    this.balls.push(
      new CannonBall(
        spawn,
        velocity,
        ball_size_multiplier,
        ball_mass_multiplier,
        is_firework
      )
    );

    const direction = velocity.clone().normalize();
    const fire_position = spawn.clone().addScaledVector( direction, 0.18 );
    Vfx3DManager.addCannonFire( fire_position, direction );
  }


  private updateRecoil(
    dt: number
  ): void
  {
    const return_speed = 15;

    this.recoil = THREE.MathUtils.lerp(
      this.recoil,
      0,
      return_speed * dt
    );

    // Preserve the crown-anchor offset and apply recoil relative to it.
    this.barrel.position.copy( this.model_base_position );
    this.barrel.position.z += this.recoil;
  }

  public setBigBallChargeActive( active: boolean ): void
  {
    this.big_ball_charge_target = active ? 1 : 0;
  }

  private updateBigBallCharge( dt: number ): void
  {
    this.normal_shot_charge = THREE.MathUtils.damp(
      this.normal_shot_charge,
      1,
      Cannon.NORMAL_SHOT_RECHARGE_DAMPING,
      dt
    );
    if ( Math.abs( 1 - this.normal_shot_charge ) < 0.001 )
      this.normal_shot_charge = 1;

    const damping = this.big_ball_charge_target > this.big_ball_charge
      ? Cannon.BIG_BALL_CHARGE_DAMPING
      : Cannon.BIG_BALL_RELEASE_DAMPING;
    this.big_ball_charge = THREE.MathUtils.damp(
      this.big_ball_charge,
      this.big_ball_charge_target,
      damping,
      dt
    );

    if ( Math.abs( this.big_ball_charge - this.big_ball_charge_target ) < 0.001 )
      this.big_ball_charge = this.big_ball_charge_target;

    this.applyBigBallMorph();
  }

  private applyBigBallMorph(): void
  {
    for ( const morph of this.big_ball_morphs )
    {
      const normal_shot_weight = this.normal_shot_charge * morph.normal_shot_strength;
      const big_ball_weight = this.big_ball_charge * morph.big_ball_strength;
      morph.influences[morph.index] = Math.max( normal_shot_weight, big_ball_weight );
    }
  }

  public static composedPivot(): THREE.Vector3
  {
    return Cannon.POSITION.clone().add( Cannon.ROTATION_ANCHOR );
  }

  public followView( to_view: ( point: THREE.Vector3 ) => THREE.Vector3 ): void
  {
    this.pivot.position.copy( to_view( Cannon.composedPivot() ) );
  }

  // Balls whose horizontal distance along `dir` exceeds `limit` are faded out.
  public setFarLimit( dir: THREE.Vector3, limit: number ): void
  {
    this.far_dir.copy( dir );
    this.far_limit = limit;
  }

  public hasActiveBalls(): boolean
  {
    return this.balls.length > 0;
  }

  public forceFadeOutBalls(): void
  {
    for ( const ball of this.balls )
    {
      // Fireworks fired from the last available shot still need enough time
      // to reach their selected targets after the booster finishes emitting.
      if ( !ball.isFirework )
        ball.forceFadeOut();
    }
  }

  public update( dt: number ): void
  {
    this.updateRecoil( dt );
    this.updateBigBallCharge( dt );

    // Pitch and yaw both use the crown-side anchor configured in setupPivot.
    this.pitch_pivot.rotation.x = this.barrel_pitch;

    let write = 0;
    for ( let i = 0; i < this.balls.length; i++ )
    {
      const ball = this.balls[i];
      if ( !ball.alive )
        continue;

      const position = ball.body.translation();
      if ( this.far_dir.x * position.x + this.far_dir.z * position.z > this.far_limit )
        ball.vanish();
      ball.update( dt );
      if ( ball.alive )
        this.balls[write++] = ball;
    }
    this.balls.length = write;
  }

  public getBalls(): CannonBall[]
  {
    return this.balls;
  }

  // Aiming and recoil move the barrel without touching any physics body, so the
  // shadow refresh check needs them reported separately.
  public hasPoseChanged(): boolean
  {
    const pitch = this.barrel_pitch;
    const yaw = this.pivot.rotation.y;
    const recoil = this.recoil;
    const normal_shot_charge = this.normal_shot_charge;
    const big_ball_charge = this.big_ball_charge;

    const changed =
      pitch !== this.last_pose_pitch ||
      yaw !== this.last_pose_yaw ||
      Math.abs( recoil - this.last_pose_recoil ) > 0.0001 ||
      Math.abs( normal_shot_charge - this.last_pose_normal_shot_charge ) > 0.0001 ||
      Math.abs( big_ball_charge - this.last_pose_big_ball_charge ) > 0.0001;

    this.last_pose_pitch = pitch;
    this.last_pose_yaw = yaw;
    this.last_pose_recoil = recoil;
    this.last_pose_normal_shot_charge = normal_shot_charge;
    this.last_pose_big_ball_charge = big_ball_charge;

    return changed;
  }

  public dispose(): void
  {
    for ( const ball of this.balls )
    {
      ball.destroy();
    }

    this.balls.length = 0;

    // this.trajectory.dispose();

    SceneManager.world.remove(
      this.pivot
    );

  }
}
